import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ShieldCheckIcon } from '@heroicons/react/24/outline';
import SreInvestigateButton from '../components/SreInvestigateButton';
import SreInvestigatePanel from '../components/SreInvestigatePanel';
import InvestigationReport from '../components/InvestigationReport';
import { SAMPLE_INVESTIGATION_REPORT } from '../fixtures/sampleInvestigationReport';
import { describeInvestigationMode } from '../services/sreInvestigationService';
import { getSreInvestigationMode } from '../constants';
import { ensureSreFontsLoaded } from '../styles/theme';
import '../styles/sre-investigation.css';
import type { TicketEntityType } from '../types';

/**
 * Standalone Investigation workbench page — Pulse visual identity.
 */
const SreInvestigationPage: React.FC = () => {
  const [params] = useSearchParams();
  const [panelOpen, setPanelOpen] = useState(false);

  useEffect(() => {
    ensureSreFontsLoaded();
  }, []);

  const target = useMemo(() => {
    const ticketNumber = params.get('ticketNumber') || 'DEMO-CASE-001';
    const entityType = (params.get('entityType') as TicketEntityType) || 'case';
    const entityId = params.get('entityId') || undefined;
    const alertId = params.get('alertId') || undefined;
    const engagementId = params.get('engagementId') || undefined;
    const ticketState = params.get('ticketState') || 'in_progress';
    return {
      ticketNumber,
      entityType,
      entityId,
      alertId,
      engagementId,
      ticketState,
    };
  }, [params]);

  const mode = getSreInvestigationMode();

  return (
    <div className="sre-wb mx-auto max-w-5xl space-y-6 p-4 font-['DM_Sans',ui-sans-serif,system-ui,sans-serif] sm:p-6">
      <header className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_1px_0_rgba(15,23,42,0.04)]">
        <div
          className="absolute inset-0 opacity-80"
          style={{
            backgroundImage:
              'radial-gradient(ellipse at 0% 0%, rgba(27,78,255,0.14), transparent 50%), radial-gradient(ellipse at 100% 0%, rgba(201,180,1,0.16), transparent 45%), linear-gradient(180deg, #f8fafc 0%, #ffffff 55%)',
          }}
        />
        <div className="relative px-6 py-7 sm:px-8">
          <div className="mb-3 flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary-600 text-white shadow-md shadow-primary-600/25">
              <ShieldCheckIcon className="h-5 w-5" />
            </span>
            <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-primary-600">
              Pulse SRE
            </span>
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
            Investigation workbench
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-600">
            Structured alert and incident analysis — metrics, evidence trace, and a clear
            root-cause report. Built for Pulse operators, not a generic agent console.
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-2">
            <span className="rounded-md border border-slate-200 bg-white/90 px-2.5 py-1 text-xs font-medium text-slate-600">
              Mode · {describeInvestigationMode()}
            </span>
            <span className="rounded-md border border-slate-200 bg-white/90 px-2.5 py-1 font-['IBM_Plex_Mono',ui-monospace,monospace] text-xs font-medium text-slate-700">
              {target.ticketNumber}
            </span>
          </div>
        </div>
      </header>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_1px_0_rgba(15,23,42,0.04)] sm:p-6">
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
          Launch
        </h2>
        <p className="mt-1 text-sm text-slate-600">
          Open the workbench to run a demo investigation with live metrics, a structured report,
          and a numbered execution trace.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <SreInvestigateButton {...target} forceShow onPosted={() => undefined} />
          <button
            type="button"
            onClick={() => setPanelOpen(true)}
            className="rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
          >
            Open panel manually
          </button>
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="h-px flex-1 bg-slate-200" />
          <h2 className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
            Sample report preview
          </h2>
          <span className="h-px flex-1 bg-slate-200" />
        </div>
        <p className="text-sm text-slate-500">
          Static OpenSRE-compatible report schema
          {mode === 'live'
            ? ' — live mode replaces this with agent output'
            : ' rendered in the Pulse workbench layout'}
          .
        </p>
        <InvestigationReport report={SAMPLE_INVESTIGATION_REPORT} />
      </section>

      <SreInvestigatePanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        target={target}
        autoStart={false}
      />
    </div>
  );
};

export default SreInvestigationPage;
